import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { SessionCredentials, SessionFilters, SessionStateDto } from '@filmsloth/shared';
import { api, type CatalogMeta } from '../api/client.js';
import { FilterEditor } from '../components/FilterEditor.js';
import { FilterPreview } from '../components/FilterPreview.js';
import { useT } from '../i18n/index.js';
import { useAccount } from '../session/useAccount.js';
import { useFilterPreview } from '../session/useFilterPreview.js';
import { readLastName, rememberName } from '../lib/lastName.js';

/** Matches the field's own `maxLength`. */
const NAME_MAX_LENGTH = 24;

interface CreateScreenProps {
  onBack: () => void;
  onCreated: (credentials: SessionCredentials, state: SessionStateDto) => void;
}

const DEFAULT_FILTERS: SessionFilters = {
  providers: [],
  genres: [],
  maxRuntimeMinutes: null,
  country: 'DE',
};

export function CreateScreen({ onBack, onCreated }: CreateScreenProps) {
  const t = useT();
  const account = useAccount();
  const [meta, setMeta] = useState<CatalogMeta | null>(null);
  const [name, setName] = useState(() => readLastName(NAME_MAX_LENGTH));
  const [filters, setFilters] = useState<SessionFilters>(DEFAULT_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .catalogMeta()
      .then(setMeta)
      .catch(() => setError(t('error.generic')));
  }, [t]);

  // Signed-in accounts get their saved streaming services and genres as a
  // starting point, not the blank slate an anonymous visitor sees — set once
  // the account has resolved, so it does not clobber filters the host has
  // already started picking.
  const seededDefaults = useRef(false);
  useEffect(() => {
    if (seededDefaults.current || account.loading) return;
    seededDefaults.current = true;
    const prefs = account.account;
    if (prefs && (prefs.preferredProviders.length > 0 || prefs.preferredGenres.length > 0)) {
      setFilters((current) => ({
        ...current,
        providers: prefs.preferredProviders,
        genres: prefs.preferredGenres,
      }));
    }
  }, [account.loading, account.account]);

  // Signed in people get their username here for free instead of typing a
  // name again — still just a starting point, since the same person picks
  // different names in different sessions.
  useEffect(() => {
    if (account.account && name.length === 0) {
      setName(account.account.username.slice(0, NAME_MAX_LENGTH));
    }
    // Keyed on the username alone: `name` is read only to avoid clobbering
    // something already typed, and listing it would re-run this on every
    // keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.account?.username]);

  // Live feedback on how much the current filters leave to swipe through.
  const preview = useFilterPreview(filters);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { credentials, state } = await api.createSession({ hostName: name, filters });
      rememberName(name);
      onCreated(credentials, state);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  const blocker =
    name.trim().length === 0
      ? t('create.needName')
      : filters.providers.length === 0
        ? t('create.needProvider')
        : null;

  return (
    <form className="screen" onSubmit={submit}>
      <div className="row row--between">
        <h1>{t('create.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="host-name">
          {t('create.yourName')}
        </label>
        <input
          id="host-name"
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('create.namePlaceholder')}
          maxLength={NAME_MAX_LENGTH}
          autoComplete="nickname"
        />
      </div>

      {meta ? (
        <FilterEditor meta={meta} filters={filters} onChange={setFilters} />
      ) : (
        <p className="muted">{t('common.loading')}</p>
      )}

      <FilterPreview preview={preview} />

      {error ? <p className="banner banner--error">{error}</p> : null}

      {/* Pinned to the bottom edge: the genre and length pickers push the
          button well below the fold, and the live count next to it is the
          answer to "is this enough?" while the filters are being picked. */}
      <div className="sticky-cta">
        <button
          type="submit"
          className="btn btn--primary btn--lg btn--block"
          disabled={busy || blocker !== null || preview?.count === 0}
        >
          {busy ? t('common.loading') : t('create.submit')}
        </button>
        {/* A greyed-out button with no reason is a riddle. */}
        {blocker ? <p className="muted small center">{blocker}</p> : null}
      </div>
    </form>
  );
}
